/**
 * API Service Layer
 * Handles all backend API calls
 */

import axios from 'axios'

// Smart API URL selection - Uses Vite's MODE to detect environment
// MODE is 'development' during `npm run dev` and 'production' during `npm run build`
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.MODE === 'production'
    ? 'https://us-central1-startuphive-dlr-dev.cloudfunctions.net'
    : 'http://localhost:5001/startuphive-dlr-dev/us-central1'
  )

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30 seconds
})

/**
 * Submit a new request
 * @param {Object} formData - Complete form data
 * @returns {Promise<Object>} { requestId, submittedAt }
 */
export const submitRequest = async (formData) => {
  try {
    const response = await apiClient.post('/api/submitRequest', { formData })
    return response.data
  } catch (error) {
    console.error('Submit request error:', error)
    throw new Error(error.response?.data?.message || 'Failed to submit request')
  }
}

/**
 * Get a request by ID
 * @param {string} requestId - Request ID (e.g., DLR-2025-001)
 * @returns {Promise<Object>} Request data
 */
export const getRequest = async (requestId) => {
  try {
    const response = await apiClient.get(`/api/getRequest/${requestId}`)
    return response.data
  } catch (error) {
    console.error('Get request error:', error)
    throw new Error(error.response?.data?.message || 'Failed to load request')
  }
}

/**
 * Update an existing request
 * @param {string} requestId - Request ID
 * @param {Object} formData - Updated form data
 * @returns {Promise<Object>} Updated request
 */
export const updateRequest = async (requestId, formData) => {
  try {
    const response = await apiClient.put(`/api/updateRequest/${requestId}`, { formData })
    return response.data
  } catch (error) {
    console.error('Update request error:', error)
    throw new Error(error.response?.data?.message || 'Failed to update request')
  }
}

/**
 * Get a request by access token
 * @param {string} token - Access token
 * @returns {Promise<Object>} Request data
 */
export const getRequestByToken = async (token) => {
  try {
    const response = await apiClient.get(`/api/getRequestByToken/${token}`)
    return response.data
  } catch (error) {
    console.error('Get request by token error:', error)
    throw new Error(error.response?.data?.message || 'Failed to load request')
  }
}

export default {
  submitRequest,
  getRequest,
  getRequestByToken,
  updateRequest
}
